import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MessageSquare, ChevronDown } from 'lucide-react';
import { navLinks, siteConfig } from '../data';
import AnnouncementBar from './AnnouncementBar';

export default function Navbar() {
  const [isOpen, setIsOpen] = React.useState(false);
  const location = useLocation();

  const handleLogoClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsOpen(false);
  };

  return (
    <header className="sticky top-0 z-50">
      <AnnouncementBar />
      <nav className="bg-white border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" onClick={handleLogoClick} className="flex items-center">
              <img 
                src="https://assets.auxodev.dk/assets/2cadb5d8-c2c5-4850-ae22-0167799adce5/68ad25eb-0941-4382-bc74-28af4d9d30b4.png" 
                alt="Online Tryghed Logo" 
                className="h-12 w-auto object-contain"
              />
            </Link>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-stretch space-x-8">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.href;
              return (
                <div key={link.name} className="relative group flex items-center">
                  <Link
                    to={link.href}
                    className={`flex items-center transition-colors h-full py-2 ${
                      isActive 
                        ? 'text-[#103B66] font-bold border-b-2 border-[#103B66]' 
                        : 'text-slate-600 hover:text-[#103B66] font-medium'
                    }`}
                  >
                    {link.name}
                    {link.dropdown && <ChevronDown className="ml-1 w-4 h-4 transition-transform group-hover:rotate-180" />}
                  </Link>
                  
                  {link.dropdown && (
                    <div className="absolute left-0 top-full w-56 bg-white border border-slate-100 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <div className="py-2">
                        {link.dropdown.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className={`block px-4 py-3 text-sm transition-colors ${
                              location.pathname === subItem.href
                                ? 'bg-slate-50 text-[#103B66] font-bold'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-[#103B66]'
                            }`}
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="flex items-center space-x-2.5">
              <a
                href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center px-4 py-2.5 bg-[#103B66] text-white font-semibold hover:bg-slate-800 hover:-translate-y-0.5 transition-all shadow-sm text-sm"
              >
                <Phone className="w-4 h-4 mr-2" />
                {siteConfig.phone}
              </a>
              <a
                href={`sms:${siteConfig.sms.replace(/\s/g, '')}`}
                className="inline-flex items-center px-4 py-2.5 bg-slate-100 text-slate-800 font-semibold hover:bg-slate-200 hover:-translate-y-0.5 transition-all shadow-sm text-sm border border-slate-200"
              >
                <MessageSquare className="w-4 h-4 mr-2 text-[#103B66]" />
                {siteConfig.sms}
              </a>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(true)}
              className="text-slate-600 p-2"
              aria-label="Åbn menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      <div 
        className={`fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] transition-opacity duration-300 md:hidden ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsOpen(false)}
      />

      {/* Mobile Sidebar */}
      <div 
        className={`fixed inset-y-0 right-0 w-[280px] bg-white z-[70] shadow-2xl transform transition-transform duration-300 ease-in-out md:hidden ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between px-6 h-20 border-b border-slate-100">
            <Link 
              to="/" 
              className="flex items-center"
              onClick={handleLogoClick}
            >
              <img 
                src="https://assets.auxodev.dk/assets/2cadb5d8-c2c5-4850-ae22-0167799adce5/68ad25eb-0941-4382-bc74-28af4d9d30b4.png" 
                alt="Online Tryghed Logo" 
                className="h-10 w-auto object-contain"
              />
            </Link>
            <button
              onClick={() => setIsOpen(false)}
              className="text-slate-500 p-2 hover:bg-slate-100 rounded-none transition-colors"
              aria-label="Luk menu"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Sidebar Links */}
          <div className="flex-1 overflow-y-auto py-6 px-6 space-y-2">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.href;
              return (
                <div key={link.name} className="space-y-1">
                  <Link
                    to={link.href}
                    className={`flex items-center justify-between py-3 text-lg transition-colors ${
                      isActive 
                        ? 'font-bold text-[#103B66]' 
                        : 'font-semibold text-slate-800 hover:text-[#103B66]'
                    }`}
                    onClick={() => !link.dropdown && setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                  {link.dropdown && (
                    <div className="pl-4 space-y-1 border-l-2 border-slate-100 ml-1">
                      {link.dropdown.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className={`block py-2.5 text-base transition-colors ${
                            location.pathname === subItem.href
                              ? 'font-bold text-[#103B66]'
                              : 'text-slate-600 hover:text-[#103B66]'
                          }`}
                          onClick={() => setIsOpen(false)}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Sidebar Footer */}
          <div className="p-6 border-t border-slate-100 bg-slate-50">
            <div className="grid grid-cols-2 gap-3">
              <a
                href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}
                className="flex flex-col items-center justify-center p-4 bg-white border border-slate-200 rounded-none text-[#103B66] shadow-sm active:scale-95 transition-all"
              >
                <Phone className="w-5 h-5 mb-2" />
                <span className="text-xs font-bold uppercase tracking-wider">Ring</span>
              </a>
              <a
                href={`sms:${siteConfig.sms.replace(/\s/g, '')}`}
                className="flex flex-col items-center justify-center p-4 bg-[#103B66] text-white rounded-none shadow-sm active:scale-95 transition-all"
              >
                <MessageSquare className="w-5 h-5 mb-2" />
                <span className="text-xs font-bold uppercase tracking-wider">SMS</span>
              </a>
            </div>
            <p className="mt-6 text-center text-xs text-slate-400 font-medium">
              Vi sidder klar alle dage 9-19
            </p>
          </div>
        </div>
      </div>
    </nav>
    </header>
  );
}
